import { setProp } from "Store/Actions";
import { store } from "Store/mainStore";
import type { AppDispatch } from "Store/mainStore";

export const PRICELIST_ERROR_FEEDBACK_TYPE = "pricelistError";

type DatamartApiShape = {
  status?: boolean | number;
  errorMessage?: string;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readErrorMessage = (record: Record<string, unknown>): string | undefined => {
  const direct =
    record.errorMessage ??
    record.message ??
    record.error_message ??
    record.error;
  if (typeof direct === "string" && direct.trim()) {
    return direct;
  }
  for (const [key, value] of Object.entries(record)) {
    if (
      key.toLowerCase() === "errormessage" &&
      typeof value === "string" &&
      value.trim()
    ) {
      return value;
    }
  }
  return undefined;
};

const readStacktrace = (record: Record<string, unknown> | null): string | undefined => {
  if (!record) {
    return undefined;
  }
  const stack = record.stacktrace ?? record.stackTrace;
  return typeof stack === "string" && stack.trim() ? stack : undefined;
};

const mentionsPricelist = (text: string | undefined): boolean =>
  typeof text === "string" && text.toLowerCase().includes("pricelist");

/** Reads status/errorMessage whether datamart returns them at the root or under `data`. */
export function normalizeSubmitWaterfallResponse(
  response: unknown,
): DatamartApiShape {
  const root = asRecord(response);
  if (!root) {
    return {};
  }

  const nested = asRecord(root.data);
  const errorMessage =
    readErrorMessage(root) ??
    (nested ? readErrorMessage(nested) : undefined);

  const status = root.status ?? nested?.status;

  return {
    status: status as boolean | number | undefined,
    errorMessage,
  };
}

export function isSubmitWaterfallPricelistError(response: unknown): boolean {
  const { status, errorMessage } = normalizeSubmitWaterfallResponse(response);
  if (status === true || status === 200) {
    return false;
  }
  const root = asRecord(response);
  const nested = asRecord(root?.data);
  const stacktrace =
    readStacktrace(root) ?? readStacktrace(nested) ?? undefined;
  return (
    mentionsPricelist(errorMessage) || mentionsPricelist(stacktrace)
  );
}

export function getSubmitWaterfallPricelistErrorMessage(response: unknown): string {
  const message = normalizeSubmitWaterfallResponse(response).errorMessage;
  if (typeof message === "string" && message.trim()) {
    return message;
  }
  const root = asRecord(response);
  const nested = asRecord(root?.data);
  const stacktrace =
    readStacktrace(root) ?? readStacktrace(nested) ?? undefined;
  if (typeof stacktrace === "string" && stacktrace.trim()) {
    return stacktrace.split("\n")[0].trim();
  }
  return "Pricelist error";
}

export function applyConfiguratorSubmitWaterfallPricelistError(
  dispatch: AppDispatch,
  errorMessage: string,
): void {
  const existing = store.getState().dynamic?.data?.pageFeedbacks ?? [];
  const pageFeedbacks = existing.filter(
    (feedback: { type: string }) =>
      feedback.type !== PRICELIST_ERROR_FEEDBACK_TYPE,
  );
  pageFeedbacks.push({
    type: PRICELIST_ERROR_FEEDBACK_TYPE,
    variant: "error",
    message: errorMessage,
  });
  dispatch(
    setProp({ slice: "dynamic", key: "pageFeedbacks", value: pageFeedbacks }),
  );
  dispatch(
    setProp({
      slice: "dynamic",
      key: "validationDetails",
      value: { disableNext: true, validateResponse: [] },
    }),
  );
  dispatch(setProp({ slice: "dynamic", key: "actionNext", value: false }));
}

export function clearConfiguratorSubmitWaterfallPricelistError(
  dispatch: AppDispatch,
): void {
  const existing = store.getState().dynamic?.data?.pageFeedbacks ?? [];
  const pageFeedbacks = existing.filter(
    (feedback: { type: string }) =>
      feedback.type !== PRICELIST_ERROR_FEEDBACK_TYPE,
  );
  if (pageFeedbacks.length !== existing.length) {
    dispatch(
      setProp({ slice: "dynamic", key: "pageFeedbacks", value: pageFeedbacks }),
    );
  }
}
